import { useMemo } from 'react';

import { createLanguageData } from '../LanguageSelector.config';
import {
  type UseLanguageSelectorParams,
  type UseLanguageSelectorReturn,
} from '../LanguageSelector.types';
import {
  filterLanguageData,
  findSelectedLanguageData,
} from '../utilities/LanguageSelector.helpers';

import useLanguagePreferencePopover from './useLanguagePreferencePopover';

/**
 * Custom hook for managing language selection
 *
 * This hook handles:
 * - Language preference management
 * - Filtering available languages
 * - Finding selected language data
 * - Language change callbacks
 *
 * @param params - Configuration parameters for the language selector
 * @returns Object containing language state and handlers
 *
 * @example
 * ```typescript
 * function LanguageSelector() {
 *   const {
 *     selectedLanguage,
 *     availableLanguages,
 *     selectedLanguageData,
 *     handleSelectLanguage
 *   } = useLanguageSelector({
 *     labelDict: DEFAULT_LABEL_DICT,
 *     languages: ['en', 'fr'],
 *     errorText: 'Language error'
 *   });
 *
 *   return (
 *     <select onChange={(e) => handleSelectLanguage(e.target.value)}>
 *       {availableLanguages.map(lang => (
 *         <option key={lang.language} value={lang.language}>
 *           {lang.label}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export const useLanguageSelector = ({
  labelDict,
  languages,
  errorText,
}: UseLanguageSelectorParams): UseLanguageSelectorReturn => {
  const {
    currentLanguage: selectedLanguage,
    changeLanguage: handleLanguageChange,
  } = useLanguagePreferencePopover({ errorText });

  /**
   * Memoized available languages based on provided filters
   * Recalculates when labelDict or languages filter changes
   */
  const availableLanguages = useMemo(() => {
    const allLanguageData = createLanguageData(labelDict);
    return filterLanguageData(allLanguageData, languages);
  }, [labelDict, languages]);

  /**
   * Memoized selected language data
   * Recalculates when available languages or selected language changes
   */
  const selectedLanguageData = useMemo(() => {
    return findSelectedLanguageData(availableLanguages, selectedLanguage);
  }, [availableLanguages, selectedLanguage]);

  /**
   * Handle language selection with automatic popover closing
   * @param language - Language code to select
   */
  const handleSelectLanguage = (language: string) => {
    handleLanguageChange(language);
  };

  return {
    selectedLanguage,
    availableLanguages,
    selectedLanguageData,
    handleSelectLanguage,
  };
};
